import { CancelToken } from 'axios';
import { provideAppName } from 'src/utils/Domain/Perspective';

import {
  OverTimeType,
  Pivot,
  ListDataOptions,
  BasicPivotItem,
  RegionItem,
  TimeSheetInfo,
  SubmitPayload,
  FitViewOptions,
  PivotServiceType,
} from 'src/worker/pivotWorker.types';
import { CoarseEditPayload, GranularEditPayloadItem, PivotClient, RedecoratePayload } from 'src/dao/pivotClient';
import { makePivotServiceCache, RequestEndpoint } from './pivotServiceCache';
import { PivotServiceCache } from './pivotServiceCache';
import { currentBearerToken } from 'src/services/axios';

import { AppType } from 'configuration-schemas/general';

const pWorker = new ComlinkWorker<typeof import('../worker/pivotWorker')>(
  new URL('../worker/pivotWorker', import.meta.url),
  {
    type: 'module',
  },
);

interface CacheHit {
  cacheData: Pivot;
  cacheHit: true;
  cacheHash: string;
}
interface CacheMiss {
  cacheHit: false;
  cacheData: {
    tree: [];
    flat: [];
  };
  cacheHash: null;
}
type CacheCheck = CacheHit | CacheMiss;

export interface CacheCheckResponse {
  cacheHash: string;
  cachePromise: Promise<CacheCheck>;
  dataPromise: Promise<Pivot>;
}

interface ServiceFitViewOptions {
  flowStatus: string;
  sortBy?: string;
  cancelToken?: CancelToken;
  groupBy?: string;
  extraParams?: {
    [s: string]: string | undefined;
  };
}
// api/property
export interface PivotService {
  pivotCache: PivotServiceCache;
  clearPivotCache(): void;
  fitViewCacheCheck(defnId: string, options?: FitViewOptions, cancelToken?: CancelToken): Promise<CacheCheckResponse>;
  listFitViewData(
    defnId: string,
    _appName: AppType,
    options?: ServiceFitViewOptions,
    allowCache?: boolean,
  ): Promise<Pivot>;
  deserialize(pivotQuery: string): Promise<Record<string, any>>;
  listDataCacheCheck(defnId: string, options: ListDataOptions): Promise<CacheCheckResponse>;
  listData(defnId: string, _appName?: AppType, options?: ListDataOptions, allowCache?: boolean): Promise<Pivot>;
  getStylePaneDetails(defnId: string, options?: ListDataOptions): Promise<BasicPivotItem[]>;
  getValidMembers(level: string): Promise<RegionItem[]>;
  getFlowSheetByIndex(memberIds: string[], start: string, end: string): Promise<Pivot>;
  getOverTimeData(memberId: string, overtimeType: OverTimeType, overrideDefnId?: string): Promise<CacheCheckResponse>;
  getMappingTimeInfo(parentLevel?: string): Promise<TimeSheetInfo>;
  submitFlowSheetPayload(payload: SubmitPayload): Promise<boolean>;
  submitPricingPayload(payload: SubmitPayload): Promise<boolean>;
  coarseEditSubmitData(payload: CoarseEditPayload): Promise<boolean>;
  granularEditSubmitData(payload: GranularEditPayloadItem[]): Promise<boolean>;
  asstAddSearchSubmitData(payload: string[], level: string): Promise<RegionItem[]>;
  asstUploadExcelAdd(payload: FormData): Promise<RegionItem[]>;
  redecorate(payload: RedecoratePayload, cancelToken?: CancelToken): Promise<BasicPivotItem[]>;
}

function generateCachePromise(pivotCache: PivotServiceCache, cacheHash: string) {
  const cacheCheck: CacheCheck = pivotCache.has(cacheHash)
    ? {
        cacheHit: true,
        cacheData: pivotCache.get(cacheHash) as Pivot,
        cacheHash: cacheHash,
      }
    : {
        cacheHit: false,
        cacheData: {
          tree: [],
          flat: [],
        },
        cacheHash: null,
      };

  return Promise.resolve(cacheCheck);
}

async function generatePivotDataPromise(
  dataPromiseLiveCallable: () => Promise<Pivot>,
  pivotCache: PivotServiceCache,
  cacheHash: string,
) {
  let dataPromise: Promise<Pivot>;

  if (pivotCache.isInflight(cacheHash)) {
    dataPromise = pivotCache.getInflightPromise(cacheHash) || Promise.resolve();
  } else {
    dataPromise = dataPromiseLiveCallable()
      .then((data) => {
        pivotCache.deleteInflightRequest(cacheHash);
        pivotCache.set(cacheHash, data);

        // TODO: we won't really need to return data here anymore with
        // newer solution of removing data from each slice and only storing in cache
        return data;
      })
      .catch((error) => {
        // TODO: not sure what to do with errors in cache, just removes inflight and propagates error
        pivotCache.deleteInflightRequest(cacheHash);
        throw error;
      });
    pivotCache.setInflightRequest(cacheHash, dataPromise);
  }

  return dataPromise;
}

export function makePivotService(type: PivotServiceType, pivotClient: PivotClient): PivotService {
  // create web worker instance

  currentBearerToken.subscribe((token) => pWorker.receiveAccessToken(type, token));

  const pivotCache = makePivotServiceCache();

  return {
    pivotCache, // warning: globally accessible through the service container
    clearPivotCache() {
      pivotCache.clear();
    },
    deserialize(pivotQuery: string) {
      return pivotClient.objectDeserializer.deserialize(pivotQuery);
    },
    asstAddSearchSubmitData(ids: string[], level: string) {
      return pivotClient.asstAddSearchSubmitData(ids, level);
    },
    asstUploadExcelAdd(file: FormData) {
      return pivotClient.asstUploadExcelAdd(file);
    },
    redecorate(payload: RedecoratePayload, cancelToken?: CancelToken) {
      if (payload.coordinates.length <= 0) {
        // eslint-disable-next-line no-console
        console.warn('No coordinates to redecorate.');
        return Promise.resolve([]);
      }
      return pivotClient.redecorate(payload, cancelToken);
    },
    listDataCacheCheck(defnId: string, inputs: ListDataOptions): Promise<CacheCheckResponse> {
      return provideAppName(async (appName: AppType) => {
        const cacheHash = PivotServiceCache.hash({ endpoint: RequestEndpoint.listData, defnId, inputs, appName });
        const dataPromiseLiveCallable = () => pWorker.getListData(appName, defnId, inputs);
        return {
          cacheHash,
          cachePromise: generateCachePromise(pivotCache, cacheHash),
          dataPromise: generatePivotDataPromise(dataPromiseLiveCallable, pivotCache, cacheHash),
        };
      });
    },
    fitViewCacheCheck(defnId: string, inputs: FitViewOptions, cancelToken: CancelToken): Promise<CacheCheckResponse> {
      return provideAppName(async (appName: AppType) => {
        const cacheHash = PivotServiceCache.hash({
          endpoint: RequestEndpoint.fitView,
          defnId,
          inputs,
          appName,
        });
        const dataPromiseLiveCallable = () => pWorker.getListFitViewData(appName, defnId, { ...inputs, cancelToken });

        return {
          cacheHash,
          cachePromise: generateCachePromise(pivotCache, cacheHash),
          dataPromise: generatePivotDataPromise(dataPromiseLiveCallable, pivotCache, cacheHash),
        };
      });
    },
    listFitViewData(
      defnId: string,
      _appName: AppType,
      options: ServiceFitViewOptions = { flowStatus: '' },
      allowCache = false,
    ) {
      return provideAppName(async (appName) => {
        const { sortBy, groupBy = '', flowStatus, cancelToken, extraParams } = options;
        const sendOpts = {
          groupBy,
          sortBy,
          flowStatus,
          cancelToken,
          ...extraParams,
        };

        const cacheHash = PivotServiceCache.hash({
          endpoint: RequestEndpoint.fitView,
          defnId,
          inputs: sendOpts,
          appName,
        });

        if (allowCache === true && pivotCache.has(cacheHash)) {
          return Promise.resolve(pivotCache.get(cacheHash));
        } else if (pivotCache.isInflight(cacheHash)) {
          return await pivotCache.getInflightPromise(cacheHash);
        }

        const prom = pWorker.getListFitViewData(appName, defnId, options);
        pivotCache.setInflightRequest(cacheHash, prom);
        const result = await prom;
        pivotCache.inflightRequestMap.delete(cacheHash);
        pivotCache.set(cacheHash, result);
        return result;
      });
    },
    listData(defnId: string, _appName: AppType = AppType.Assortment, options: ListDataOptions, allowCache = false) {
      return provideAppName(async (appName) => {
        const formattedOptions = { ...options, appName };
        const cacheHash = PivotServiceCache.hash({
          endpoint: RequestEndpoint.listData,
          defnId,
          inputs: formattedOptions,
          appName,
        });

        if (allowCache === true && pivotCache.has(cacheHash)) {
          return Promise.resolve(pivotCache.get(cacheHash));
        } else if (pivotCache.isInflight(cacheHash)) {
          return await pivotCache.getInflightPromise(cacheHash);
        }

        const prom = pWorker.getListData(appName, defnId, formattedOptions);
        pivotCache.setInflightRequest(cacheHash, prom);

        try {
          const result = await prom;
          pivotCache.set(cacheHash, result);
          return result;
        } finally {
          // always need to remove from in flight list
          pivotCache.inflightRequestMap.delete(cacheHash);
        }
      });
    },
    getStylePaneDetails(defnId: string, options: ListDataOptions) {
      return provideAppName(async (appName) => {
        const listData = await pWorker.getListData(appName, defnId, options);
        return listData.flat;
      });
    },
    getValidMembers(level: string) {
      return provideAppName(async (appName) => {
        return await pWorker.getValidMembers({ appName, level });
      });
    },
    getFlowSheetByIndex(
      memberIds: string[],
      start: string,
      end: string,
      defnId = 'FlowSheetStyleAll',
      aggBy = 'level:stylecolor',
    ) {
      return provideAppName(async (appName) => {
        const queryParams = {
          appName,
          memberIds,
          aggBy,
          defnId,
          start,
          end,
          sortBy: 'product,time',
        };
        return await pWorker.getFlowSheetByIndex(queryParams);
      });
    },
    getOverTimeData(
      memberId: string,
      overtimeType: OverTimeType,
      overrideDefnId?: string,
    ): Promise<CacheCheckResponse> {
      return provideAppName(async (appName) => {
        // Optionally, the third `componentProps.defns.model` element gets passed down through here and is used
        // as the defnId
        const defnId = overrideDefnId
          ? overrideDefnId
          : overtimeType === OverTimeType.flowsheet
            ? 'FlowSheetStyle'
            : 'PricingOvertime';
        const aggBy = 'level:stylecolor';
        const queryParams = {
          appName,
          memberId,
          aggBy,
          defnId,
          sortBy: 'stylecolor',
        };
        const endpoint = OverTimeType.flowsheet ? 'flowsheet' : 'pricing';
        const cacheHash = PivotServiceCache.hash({ endpoint, defnId, inputs: queryParams, appName });
        const dataPromiseLive = () => pWorker.getOverTimeData(queryParams, overtimeType);
        return {
          cacheHash,
          cachePromise: generateCachePromise(pivotCache, cacheHash),
          dataPromise: generatePivotDataPromise(dataPromiseLive, pivotCache, cacheHash),
        };
      });
    },
    getMappingTimeInfo(parentLevel = 'month') {
      return provideAppName(async (appName) => {
        const queryParams = {
          appName,
          parentLevel,
        };

        return await pWorker.getTimeMapping(queryParams);
      });
    },
    submitFlowSheetPayload(payload: SubmitPayload) {
      return provideAppName((appName) => {
        return pivotClient.flowSheetSubmitData(payload, {
          appName,
        });
      });
    },
    submitPricingPayload(payload: SubmitPayload) {
      return provideAppName((appName) => {
        return pivotClient.pricingSubmitData(payload, {
          appName,
        });
      });
    },
    coarseEditSubmitData(payload: CoarseEditPayload): Promise<boolean> {
      return provideAppName(async (appName) => {
        if (payload.coordinates.length <= 0) {
          // eslint-disable-next-line no-console
          console.warn('No coordinates to update.');
          return true;
        }
        return pivotClient.coarseEditSubmitData({ ...payload, appName });
      });
    },
    granularEditSubmitData(payload: GranularEditPayloadItem[]): Promise<boolean> {
      return provideAppName(async (appName) => {
        if (payload.length <= 0) {
          // eslint-disable-next-line no-console
          console.warn('No coordinates to update.');
          return true;
        }
        return pivotClient.granularEditSubmitData({ updateReqs: payload, appName });
      });
    },
  };
}
